// صفحات عمومی اشتراک (AP-PLT-07؛ C-AGENT-1155، 1166، 1164): بازگشت از درگاه پرداخت اشتراک (Verify سمت سرور؛ درگاه نمونه با نشان «نمونه»)، پذیرش مالکیت با
// OTP و کد ملی (US-PLT-007) و دانلود خروجی کامل داده با OTP موبایل مالک (US-PLT-006). همه بی‌ورود و با سقف نرخ سرور.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type OwnershipInvitationInfo } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, KeyValueList, latinDigits, LoadingState, Money, normalizeNationalId, SampleBadge, toJalali, toJalaliDateTime } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { saveBlob } from '../../medorders/common';
import { PublicShell } from './PublicShell';

export const SCR_STAFF_904_RETURN = 'SCR-STAFF-904';
export const SCR_STAFF_909_PUBLIC = 'SCR-STAFF-909';

/** بازگشت از درگاه پرداخت اشتراک ← Verify یک‌باره. */
export function SubscriptionPaymentReturnPage() {
  const { t } = useTranslation('plt');
  const { token = '' } = useParams();
  const api = usePublicApi();
  const verify = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/platform/public/subscription-payments/{token}:verify', { params: { path: { token } } })),
  });
  const started = useRef(false);
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      verify.mutate();
    }
  }, [verify]);
  const result = verify.data;
  return (
    <PublicShell screenId={SCR_STAFF_904_RETURN} titleKey="plt:payment_return.title">
      <Stack spacing={2}>
        {verify.isPending ? <Typography>{t('plt:payment_return.verifying')}</Typography> : null}
        {verify.error ? <ErrorState error={verify.error} /> : null}
        {result ? (
          <Stack spacing={2} data-testid="payment-return" data-status={result.status}>
            {result.isSample ? <SampleBadge /> : null}
            <Alert severity={result.status === 'Succeeded' ? 'success' : result.status === 'Failed' || result.status === 'Cancelled' ? 'error' : 'info'}>
              {t(`plt:payment_return.${result.status}`)}
            </Alert>
            <KeyValueList
              columns={1}
              items={[
                { labelKey: 'plt:subscription.invoices.number', value: <bdi dir="ltr">{result.invoiceNumber}</bdi> },
                { labelKey: 'plt:subscription.invoices.total', value: <Money amountMinor={result.amount.amount} currency={result.amount.currency} /> },
                { labelKey: 'plt:payment_return.reference', value: result.reference ? <bdi dir="ltr">{result.reference}</bdi> : '—' },
              ]}
            />
          </Stack>
        ) : null}
        <Button component={RouterLink} to="/settings/subscription" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
          {t('plt:payment_return.back')}
        </Button>
      </Stack>
    </PublicShell>
  );
}

/** پذیرش مالکیت با OTP و تطبیق شاهکار. */
export function OwnershipAcceptPage() {
  const { t } = useTranslation('plt');
  const { token = '' } = useParams();
  const api = usePublicApi();
  const queryClient = useQueryClient();
  const [otpId, setOtpId] = useState<string>();
  const [code, setCode] = useState('');
  const [nationalId, setNationalId] = useState('');
  const key = ['platform', 'public', 'ownership', token] as const;
  const info = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET('/api/v1/platform/public/ownership-transfers/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const send = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/platform/public/ownership-transfers/{token}:send-otp', { params: { path: { token } } })),
    onSuccess: (challenge) => setOtpId(challenge.otpId),
  });
  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/public/ownership-transfers/{token}:accept', {
          params: { path: { token } },
          body: { otpId: otpId ?? null, code, nationalId: normalizeNationalId(nationalId) ?? nationalId },
        }),
      ),
    onSuccess: (view: OwnershipInvitationInfo) => queryClient.setQueryData(key, view),
  });
  const data = info.data;
  return (
    <PublicShell screenId={SCR_STAFF_909_PUBLIC} titleKey="plt:ownership_accept.title" name={data?.tenantName}>
      {info.isPending ? <LoadingState rows={2} /> : null}
      {info.error ? <ErrorState error={info.error} /> : null}
      {data ? (
        <Stack spacing={2} data-testid="ownership-accept" data-status={data.status}>
          {data.status === 'AwaitingApproval' ? <Alert severity="success">{t('plt:ownership_accept.accepted')}</Alert> : null}
          {data.status !== 'AwaitingNewOwner' && data.status !== 'AwaitingApproval' ? <Alert severity="warning">{t('plt:ownership_accept.closed')}</Alert> : null}
          {data.status === 'AwaitingNewOwner' ? (
            <>
              <Typography>{t('plt:ownership_accept.hint', { tenant: data.tenantName, mobile: faDigits(data.maskedMobile) })}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('plt:ownership_accept.expires', { date: toJalaliDateTime(data.expiresAt) })}
              </Typography>
              <Button variant="outlined" onClick={() => send.mutate()} disabled={send.isPending} data-testid="ownership-send-otp" sx={{ alignSelf: 'flex-start' }}>
                {t('plt:ownership_accept.send_otp')}
              </Button>
              {otpId ? (
                <>
                  <TextField
                    label={t('plt:ownership_accept.code')}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', autoComplete: 'one-time-code', 'data-testid': 'ownership-code' } }}
                  />
                  <TextField
                    label={t('plt:ownership_accept.national_id')}
                    value={nationalId}
                    onChange={(event) => setNationalId(event.target.value)}
                    slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', maxLength: 10, 'data-testid': 'ownership-national-id' } }}
                  />
                  <Button variant="contained" onClick={() => accept.mutate()} disabled={!code || !nationalId || accept.isPending} data-testid="ownership-accept-submit">
                    {t('plt:ownership_accept.accept')}
                  </Button>
                </>
              ) : null}
            </>
          ) : null}
          {send.error ? <ErrorState error={send.error} compact /> : null}
          {accept.error ? <ErrorState error={accept.error} compact /> : null}
        </Stack>
      ) : null}
    </PublicShell>
  );
}

/** دانلود خروجی کامل داده با OTP موبایل مالک. */
export function ExportDownloadPage() {
  const { t } = useTranslation('plt');
  const { token = '' } = useParams();
  const api = usePublicApi();
  const [otpId, setOtpId] = useState<string>();
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const info = useQuery({
    queryKey: ['platform', 'public', 'export', token],
    queryFn: () => unwrap(api.GET('/api/v1/platform/public/exports/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const send = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/platform/public/exports/{token}:send-otp', { params: { path: { token } } })),
    onSuccess: (challenge) => setOtpId(challenge.otpId),
  });
  const download = useMutation({
    mutationFn: async () => {
      const result = await api.POST('/api/v1/platform/public/exports/{token}:download', {
        params: { path: { token } },
        body: { otpId: otpId ?? null, code },
        parseAs: 'blob',
      });
      if (!result.response.ok) {
        await unwrap(Promise.resolve(result as never));
        return false;
      }
      // نام فایل فقط ASCII (ارقام لاتین بدون نشانه جهت) تا مرورگر آن را به «download» برنگرداند.
      saveBlob(result.data as unknown as Blob, `phoenix-export-${latinDigits(toJalali(new Date())).replace(/[^0-9]+/g, '-')}.zip`);
      return true;
    },
    onSuccess: () => setDone(true),
  });
  const data = info.data;
  return (
    <PublicShell screenId={SCR_STAFF_909_PUBLIC} titleKey="plt:export_download.title" name={data?.centerName}>
      {info.isPending ? <LoadingState rows={2} /> : null}
      {info.error ? <ErrorState error={info.error} /> : null}
      {data ? (
        <Stack spacing={2} data-testid="export-download">
          <Typography>{t('plt:export_download.hint', { tenant: data.centerName, mobile: faDigits(data.maskedMobile) })}</Typography>
          <KeyValueList
            columns={1}
            items={[
              { labelKey: 'plt:export_download.size', value: <bdi dir="ltr">{faDigits(`${(Number(data.size) / (1024 * 1024)).toFixed(2)} MB`)}</bdi> },
              { labelKey: 'plt:export_download.sha', value: <bdi dir="ltr" style={{ wordBreak: 'break-all' }}>{data.sha256}</bdi> },
              { labelKey: 'plt:export_download.expires', value: toJalaliDateTime(data.expiresAt) },
            ]}
          />
          <Button variant="outlined" onClick={() => send.mutate()} disabled={send.isPending} data-testid="export-send-otp" sx={{ alignSelf: 'flex-start' }}>
            {t('plt:export_download.send_otp')}
          </Button>
          {otpId ? (
            <>
              <TextField
                label={t('plt:export_download.code')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', autoComplete: 'one-time-code', 'data-testid': 'export-code' } }}
              />
              <Button variant="contained" onClick={() => download.mutate()} disabled={!code || download.isPending} data-testid="export-download-submit">
                {t('plt:export_download.download')}
              </Button>
            </>
          ) : null}
          {done ? <Alert severity="success">{t('plt:export_download.downloaded')}</Alert> : null}
          {send.error ? <ErrorState error={send.error} compact /> : null}
          {download.error ? <ErrorState error={download.error} compact /> : null}
        </Stack>
      ) : null}
    </PublicShell>
  );
}
